import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Crew } from '@/types';
import { formatDate } from '@/helpers/formatDate';
import { formatAddress } from '@/helpers/formatAddress';
import {
  getStatusText,
  getPriorityText,
  getCrewStatusText,
  StatusBadge,
  PriorityBadge,
} from '@/helpers/enumStylers';
import { CustomFieldDisplay } from '@/components/shared/CustomFieldDisplay';
import { useSkills } from '@/hooks/api/useSkills';
import { cn } from '@/lib/utils';
import {
  MapPin,
  CalendarDays,
  Hash,
  Briefcase,
  Building2,
  Clock,
  Layers,
  Copy,
  Check,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { FormSelect } from '@/components/shared/FormSelect';
import { FormTextarea } from '@/components/shared/FormTextArea';
import {
  TicketDetail,
  TicketPriority,
  TicketStatus,
} from '@packages/database/types';
import { useProjectTypeCustomFields } from '@/hooks/api/useProjects';
import { findActiveCrew } from '@/helpers/findActiveCrew';
import { TicketStatusDropdown } from '@/components/shared/TicketStatusDropdowm';
import { useCopy } from '@/hooks/useCopy';

const InfoBlock = ({
  label,
  value,
  icon: Icon,
  className,
}: {
  label: string;
  value: React.ReactNode;
  icon?: any;
  className?: string;
}) => (
  <div className={cn('flex flex-col gap-1.5', className)}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {Icon && <Icon size={12} />}
      {label}
    </div>
    <div className="text-sm font-medium text-foreground leading-none">
      {value || <span className="text-muted-foreground/50 italic">N/A</span>}
    </div>
  </div>
);

const LocationBlock = ({ ticket }: { ticket: TicketDetail }) => {
  // const { zoomToFeature, isLocating } = useZoomToFeature(); // TODO: Implement
  const isLocating = false; // Placeholder

  const addressString = formatAddress(
    ticket.address1,
    ticket.address2,
    ticket.city || '',
    ticket.state,
    ticket.country,
    ticket.zipcode,
  );

  const hasCoords = ticket.latitude && ticket.longitude;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Location Details
        </Label>
      </div>

      <div className="rounded-md border p-3 bg-background flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-muted p-1.5">
          <MapPin size={16} className="text-muted-foreground" />
        </div>
        <div>
          <div className="text-sm font-medium">
            {addressString || 'No physical address listed'}
          </div>
          {hasCoords && (
            <div className="text-xs text-muted-foreground mt-1 font-mono">
              {Number(ticket.latitude).toFixed(6)},{' '}
              {Number(ticket.longitude).toFixed(6)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface DetailsTabContentProps {
  ticket: TicketDetail;
  crews: Crew[];
  permissions: {
    isAdmin?: boolean;
    isCrew?: boolean;
    canEditTicket: boolean;
  };
}

export const DetailsTabContent = ({
  ticket,
  crews,
  permissions,
}: DetailsTabContentProps) => {
  const { data: skills, isLoading } = useSkills();
  // const { customFields, isLoading: areCustomFieldsLoading } = useCustomFields(
  //   {},
  // );
  const { copied, handleCopy } = useCopy();

  const { data: customFields, isLoading: areCustomFieldsLoading } =
    useProjectTypeCustomFields(ticket.project?.projectTypeId);

  const skillOptions = useMemo(() => {
    return skills?.map((skill) => ({
      value: skill.id,
      label: skill.name,
    }));
  }, [skills]);

  return (
    <div className="flex flex-col gap-6 pt-2">
      {/* PROJECT DETAILS SECTION */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Layers size={18} className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">
            Project Details
          </h3>
        </div>

        {ticket.project ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
            <InfoBlock
              label="Project Name"
              value={ticket.project.name}
              icon={Building2}
            />
            <InfoBlock
              label="Project Type"
              value={
                ticket.project.projectType?.name ? (
                  <Badge
                    variant="outline"
                    className="font-normal rounded-sm px-2 py-0 h-5"
                  >
                    {ticket.project.projectType.name}
                  </Badge>
                ) : (
                  'N/A'
                )
              }
              icon={Briefcase}
            />

            {/* Row 2: Number */}
            <InfoBlock
              label="Project Number"
              value={ticket.project.projectNumber}
              icon={Hash}
            />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            No project linked.
          </div>
        )}
      </section>

      {/* TICKET DETAILS SECTION */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Briefcase size={18} className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">
            Ticket Details
          </h3>
        </div>

        {/* Timeline Box */}
        <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg border border-dashed border-muted-foreground/20">
          {/* <InfoBlock
            label="Ticket Number"
            value={
              <span className="font-mono text-primary font-semibold">
                {ticket.ticketNumber}
              </span>
            }
            icon={Hash}
          /> */}
          <InfoBlock
            label="Ticket Number"
            value={
              <div className="group flex items-center gap-2">
                <span className="font-mono text-primary font-semibold">
                  {ticket.ticketNumber}
                </span>
                <button
                  onClick={() => handleCopy(ticket.ticketNumber)}
                  className="cursor-pointer inline-flex items-center justify-center rounded-md p-1 text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title="Copy ticket number"
                  aria-label="Copy ticket number"
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-primary transition-transform duration-200 scale-110" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 transition-all duration-200  " />
                  )}
                </button>
              </div>
            }
            icon={Hash}
          />
          <InfoBlock
            label="Created Date"
            value={formatDate(ticket.createdAt)}
            icon={CalendarDays}
          />
          <InfoBlock
            label="Start Date"
            value={formatDate(ticket.startDate)}
            icon={Clock}
          />
          <InfoBlock
            label="End Date"
            value={ticket.endDate ? formatDate(ticket.endDate) : 'Open'}
            icon={Clock}
          />
        </div>

        {/* Location Info */}
        <LocationBlock ticket={ticket} />

        {/* Status / Priority / Crew Controls */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {permissions.canEditTicket ? (
            <TicketStatusDropdown />
          ) : (
            // <FormSelect
            //   name="status"
            //   label="Status"
            //   options={Object.values(TicketStatus)
            //     .filter((s) => s !== TicketStatus.COMPLETED)
            //     .map((s) => ({ value: s, label: getStatusText(s) }))}
            // />
            <InfoBlock
              label="Status"
              value={<StatusBadge status={ticket.status} />}
            />
          )}

          {permissions.canEditTicket ? (
            <FormSelect
              name="priority"
              label="Priority"
              options={Object.values(TicketPriority).map((p) => ({
                value: p,
                label: getPriorityText(p),
              }))}
            />
          ) : (
            <InfoBlock
              label="Priority"
              value={<PriorityBadge priority={ticket.priority} />}
            />
          )}

          {permissions.canEditTicket ? (
            <FormSelect
              name="assignedCrewId"
              label="Assigned Crew"
              options={crews.map((crew) => ({
                value: crew.id,
                label: `${crew.name} | ${getCrewStatusText(
                  crew.operationalStatus,
                )}`,
              }))}
            />
          ) : (
            <InfoBlock
              label="Assigned Crew"
              // TODO: FIX for right crew
              value={
                <Badge key="{s}" variant="secondary" className="font-normal">
                  {findActiveCrew(ticket.ticketAssignments)?.name ||
                    'Unassigned'}
                </Badge>
              }
            />
          )}
        </div>

        {/* Skills (Moved Above Description) */}
        <div className="space-y-2">
          {permissions.canEditTicket ? (
            <FormSelect
              name="skillsRequired"
              label="Required Skills"
              multiple={true}
              options={skillOptions}
              placeholder="Select skills..."
            />
          ) : (
            <div className="flex flex-wrap gap-2 pt-1">
              {ticket.skillsRequired && ticket.skillsRequired.length > 0 ? (
                <InfoBlock
                  label="Skills"
                  value={ticket.skillsRequired.map((s) => (
                    <Badge
                      key={s.skillId}
                      variant="secondary"
                      className="font-normal"
                    >
                      {s.skill.name}
                    </Badge>
                  ))}
                />
              ) : (
                <InfoBlock
                  label="Required Skills"
                  value={
                    <span className="text-muted-foreground">
                      No skills specified
                    </span>
                  }
                />
              )}
            </div>
          )}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <FormTextarea
            name="description"
            label="Description"
            disabled={!permissions.canEditTicket}
            rows={5}
          />
        </div>
      </section>

      {/* CUSTOM FIELDS SECTION */}

      {!areCustomFieldsLoading &&
        Array.isArray(customFields) &&
        customFields.length > 0 && (
          <section className="space-y-4 pt-4 border-t">
            {/* <div className="flex items-center gap-2 pb-2">
              <AlertCircle size={18} className="text-primary" />
              <h3 className="text-base font-semibold text-foreground">
                Additional Information
              </h3>
            </div> */}
            <div className="grid grid-cols-2 gap-6 md:grid-cols-2">
              {customFields.map(
                (field) =>
                  field.isVisible && (
                    <CustomFieldDisplay
                      key={field.id}
                      field={field}
                      displayValue={isValidJSON(ticket?.customFieldValues)}
                      isEditMode={permissions.canEditTicket}
                      name="customFields"
                    />
                  ),
              )}
            </div>
          </section>
        )}
    </div>
  );
};

function isValidJSON(value: any) {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value; // not valid JSON
    }
  }
  return value; // already an object
}
